const COUNTRY_WEBSITE_LOOKUP = {
  'DE': 'fridaysnap.de',
  'GB': 'fridaysnap.co.uk',
  'AT': 'fridaysnap.at',
  'CH': 'friday-snap.ch',
  'NL': 'friday-snap.nl',
  'PL': 'fridaysnap.pl',
  'FR': 'friday-snap.fr',
  'ES': 'friday-snap.es',
  'BE': 'fridaysnap.be',
};

const COUNTRY_APP_LOOKUP = Object.fromEntries(
  Object.entries(COUNTRY_WEBSITE_LOOKUP).map(([key, value]) => {
    // Use template literals (backticks) for string interpolation like Python's f-strings
    return [key, `app.${value}`];
  })
);

export enum LanguageSite {
  DE = 'DE',
  GB = 'GB',
  AT = 'AT',
  CH = 'CH',
  NL = 'NL',
  PL = 'PL',
  FR = 'FR',
  ES = 'ES',
  BE = 'BE',
}

// --- Helper: Inverse Map for Efficient Hostname Lookup ---
// Creates a map from hostname (website or app) back to the LanguageSite enum member
// This is created once when the module loads for better performance in getCurrentLanguageSite
const HOSTNAME_TO_LANGUAGE_SITE_MAP: { [hostname: string]: LanguageSite } = {};

// Populate with website hostnames
for (const [countryCode, websiteHostname] of Object.entries(COUNTRY_WEBSITE_LOOKUP)) {
  // Ensure the countryCode is a valid key in the LanguageSite enum before assigning
  if (countryCode in LanguageSite) {
    HOSTNAME_TO_LANGUAGE_SITE_MAP[websiteHostname] = countryCode as LanguageSite;
  }
}

// Populate with app hostnames (potentially overwriting is fine if consistent)
for (const [countryCode, appHostname] of Object.entries(COUNTRY_APP_LOOKUP)) {
  if (countryCode in LanguageSite) {
    HOSTNAME_TO_LANGUAGE_SITE_MAP[appHostname] = countryCode as LanguageSite;
  }
}

export function getCurrentLanguageSite(): LanguageSite {
  // Check if running in a browser environment
  if (typeof window === 'undefined' || !window.location) {
    console.warn("Cannot determine language site: 'window.location' is not available. Defaulting to DE.");
    return LanguageSite.DE; // Default in non-browser or unusual contexts
  }

  const hostname = window.location.hostname;
  const detectedSite = HOSTNAME_TO_LANGUAGE_SITE_MAP[hostname];

  if (detectedSite) {
    return detectedSite;
  }

  // Handle localhost for development
  if (hostname === 'localhost' || hostname.startsWith('192.168.') || hostname.endsWith('.local')) {
    return LanguageSite.DE;
  }

  // If no match found after checking known hostnames and potential dev hosts
  console.warn(`Hostname "${hostname}" did not match any known site configuration. Defaulting to DE.`);
  return LanguageSite.DE; // Default fallback
}

// --- Site-Specific Configuration ---

// Maps LanguageSite enum member to the corresponding product page URL
// NOTE: Update placeholders for actual URLs
export const ProductPagesForLanguageSite = {
  [LanguageSite.DE]: 'https://fridaysnap.de/produkt/qr-code-hochzeit-fotos/',
  [LanguageSite.GB]: 'https://fridaysnap.co.uk/product/wedding-qr-code-photos/', // Mapped from old .co.uk
  [LanguageSite.AT]: 'https://fridaysnap.at/product/qr-code-hochzeit-fotos/',
  [LanguageSite.CH]: 'https://friday-snap.ch/product/qr-code-hochzeit-fotos/',
  [LanguageSite.NL]: 'https://friday-snap.nl/product/qr-code-hochzeit-fotos/', // Assumed pattern - VERIFY/UPDATE
  [LanguageSite.PL]: 'https://fridaysnap.pl/produkt/slubny-kod-qr-zdjecia/',
  [LanguageSite.FR]: 'https://friday-snap.fr/product/qr-code-hochzeit-fotos/', // Assumed pattern - VERIFY/UPDATE
  [LanguageSite.ES]: 'https://friday-snap.es/product/qr-code-hochzeit-fotos/', // Assumed pattern - VERIFY/UPDATE
  [LanguageSite.BE]: 'https://fridaysnap.be/product/qr-code-hochzeit-fotos/', // Assumed pattern - VERIFY/UPDATE
};

export function getCurrentLanguageInfos() {
  const lang = getCurrentLanguageSite()

  return {
    shopHomepage: `https://${COUNTRY_WEBSITE_LOOKUP[lang]}`,
    weddingProductPage: ProductPagesForLanguageSite[lang],
    email: 'hello@fridaysnap.de',
  }
}

